import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";

interface UndoDeleteBannerProps {
  open: boolean;
  message: string;
  deadline: number;
  durationMs: number;
  onUndo: () => void;
}

export default function UndoDeleteBanner({
  open,
  message,
  deadline,
  durationMs,
  onUndo,
}: UndoDeleteBannerProps) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [open]);

  const remainingMs = Math.max(0, deadline - now);
  const secondsLeft = Math.max(0, Math.ceil(remainingMs / 1000));
  const progressPct = useMemo(() => {
    if (durationMs <= 0) return 0;
    return Math.max(0, Math.min(100, (remainingMs / durationMs) * 100));
  }, [remainingMs, durationMs]);

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[120] p-3 sm:p-4" data-testid="undo-delete-banner">
      <div className="mx-auto w-full max-w-2xl rounded-lg border bg-background/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="h-1 w-full overflow-hidden rounded-t-lg bg-muted">
          <div
            className="h-full bg-amber-500 transition-[width] duration-100"
            style={{ width: `${progressPct}%` }}
            data-testid="undo-delete-progress"
          />
        </div>
        <div className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4 sm:py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{message}</p>
            <p className="text-xs text-muted-foreground">{secondsLeft}s left to undo</p>
          </div>
          <Button size="sm" variant="outline" onClick={onUndo} data-testid="button-undo-delete">
            Undo
          </Button>
        </div>
      </div>
    </div>
  );
}
